import {
  ApiErrorInfo,
  decimalPlaces,
  decimalToPercent,
  isNonNegativeDecimalString,
  percentToDecimal,
} from 'src/pages/admin/utils/decimal-input';

import {
  CLOSE_TIMEOUT_SECONDS_MAX,
  DECIMAL_MAX_DECIMALS,
  EVENT_TIME_TOLERANCE_MS_MAX,
  HL_PERP_COIN,
  HL_K_COIN,
  LOCKUP_DAYS_MAX,
  LOCKUP_DAYS_MIN,
  MAX_LEVERAGE_CEILING,
  MAX_LIVE_VAULTS_PER_MANAGER_MAX,
  MAX_LIVE_VAULTS_PER_MANAGER_MIN,
  PERCENT_MAX_DECIMALS,
  PERFORMANCE_FEE_CAP_MAX_PERCENT,
  VAULT_PARAMS_ERROR,
  VAULT_PARAMS_NS as NS,
  VENUE_LAG_ALLOWANCE_MS_MAX,
  VENUE_TIME_SKEW_BAND_MS_MAX,
} from './constants';
import { FieldKind, FieldSpec } from './fields';

export type Draft = Record<string, string | string[]>;
/** field → i18n key */
export type FieldErrors = Record<string, string>;

export interface FieldChange {
  key: string;
  label: string;
  before: string;
  after: string;
}

const EMPTY = '—';

/** Kinds sent as a JSON integer (GraphQL Int), not a decimal string. */
export const isIntegerKind = (kind: FieldKind): boolean =>
  kind === 'days' || kind === 'seconds' || kind === 'milliseconds' || kind === 'count';

/** The fields a save can carry: a read-only field is shown, never drafted, diffed or sent. */
export const editableFields = <T>(fields: FieldSpec<T>[]): FieldSpec<T>[] =>
  fields.filter(({ readOnly }) => !readOnly);

/** Removes trailing zeros so `100.000000` reads `100`. */
export const trimZeros = (value: string): string =>
  value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value;

/**
 * Pads a plain decimal string to `dp` places, e.g. `1` → `1.000000` for a NAV
 * shown at its engine precision. String-only: it never rounds, never cuts
 * digits it was given, and returns anything that is not a plain decimal as is.
 */
export const formatFixedDecimals = (value: string, dp: number): string => {
  if (!/^-?\d+(\.\d+)?$/.test(value) || dp <= 0) return value;
  const [whole, fraction = ''] = value.split('.');
  return fraction.length >= dp ? value : `${whole}.${fraction.padEnd(dp, '0')}`;
};

/** Removes leading zeros from a decimal string's whole part, e.g. `0100` → `100`,
 * `000.5` → `0.5`. A bare `0` (nothing to strip) is left as `0`. */
const trimLeadingZeros = (value: string): string => {
  const [whole, fraction] = value.split('.');
  const trimmedWhole = whole.replace(/^0+(?=\d)/, '') || '0';
  return fraction === undefined ? trimmedWhole : `${trimmedWhole}.${fraction}`;
};

/**
 * The part of a coin name the venue's case rules apply to: the whole name, or
 * the text after a HIP-3 dex prefix (`xyz:GOLD` → `GOLD`).
 */
const coinPart = (coin: string): string => coin.slice(coin.indexOf(':') + 1);

/**
 * The form a coin name is recorded in: upper case, so `btc` and `BTC` are one
 * entry (QA 2026-09-29 item 5). Hyperliquid's own names are upper case except
 * the 1000× family (kPEPE), which is kept when typed exactly — `KPEPE` is not a
 * coin, and the venue is case-sensitive. A `kpepe` cannot be told from a real
 * `k…` ticker such as KAS, so only the exact venue form is exempt.
 *
 * A HIP-3 coin is `dex:COIN` (XBIT-4794): the dex is recorded in lower case,
 * which is the only form the engine accepts, and the coin after it follows the
 * rule above. Without this the dex of `xyz:GOLD` would be upper-cased to
 * `XYZ:GOLD`, which no save accepts.
 */
export const canonicalCoin = (entry: string): string => {
  const trimmed = entry.trim();
  const colon = trimmed.indexOf(':');
  const dex = colon === -1 ? '' : `${trimmed.slice(0, colon).toLowerCase()}:`;
  const coin = coinPart(trimmed);
  return `${dex}${HL_K_COIN.test(coin) ? coin : coin.toUpperCase()}`;
};

/**
 * Splits on commas/whitespace, trims, drops blanks, canonicalises the case
 * (`canonicalCoin`) and de-dupes ignoring case, so a name differing only by
 * case is one entry. First occurrence keeps its position; when the same name
 * arrives both as `KPEPE` and `kPEPE`, the venue form wins.
 */
export const normalizeCoins = (values: string[]): string[] => {
  const byKey = new Map<string, string>();
  values
    .flatMap((entry) => String(entry).split(/[,\s]+/))
    .map(canonicalCoin)
    .filter(Boolean)
    .forEach((coin) => {
      const key = coin.toUpperCase();
      const seen = byKey.get(key);
      if (seen === undefined || (HL_K_COIN.test(coinPart(coin)) && !HL_K_COIN.test(coinPart(seen))))
        byKey.set(key, coin);
    });
  return Array.from(byKey.values());
};

export interface FieldSection<T> {
  /** i18n key of the heading; undefined for fields declared without a group. */
  group?: string;
  fields: FieldSpec<T>[];
}

/**
 * Splits a field list into consecutive runs sharing a `group`, in declared
 * order, for the read card and the edit form to render under one heading each.
 * Presentation only: flattening the result gives back the input.
 */
export const groupFields = <T>(fields: FieldSpec<T>[]): FieldSection<T>[] =>
  fields.reduce<FieldSection<T>[]>((sections, field) => {
    const last = sections[sections.length - 1];
    if (last && last.group === field.group) last.fields.push(field);
    else sections.push({ group: field.group, fields: [field] });
    return sections;
  }, []);

const FULL_WIDTH_DIGIT = /[\uFF10-\uFF19]/g;
const FULL_WIDTH_POINT = /[\u3002\uFF0E]/g;

/**
 * Numeric input for this page only. A CJK IME types full-width digits and
 * `。`/`．` for the point; those become `0-9` and `.`. Everything else is kept
 * as typed, so `1,5`, `-0.5` or `1e2` reach validation and show "not a number"
 * instead of being rewritten into a different amount (`1,5` → `15`).
 *
 * The shared `sanitizeDecimalInput` / `sanitizeIntegerInput` strip every
 * non-digit; fee-config still depends on (and tests) that, so this page uses
 * its own normaliser rather than changing them for every caller.
 */
export const normalizeNumericInput = (value: string): string =>
  value
    .replace(FULL_WIDTH_DIGIT, (ch) => String(ch.charCodeAt(0) - 0xff10))
    .replace(FULL_WIDTH_POINT, '.');

/** BFF error code → i18n key for an engine/transport failure. The upstream
 * text is never shown for these: it is English, and may name internals. */
const ENGINE_FAILURE_KEYS: Record<string, string> = {
  [VAULT_PARAMS_ERROR.ENGINE_AUTH_FAILED]: `${NS}.errors.engineAuthFailed`,
  [VAULT_PARAMS_ERROR.ENGINE_UNAVAILABLE]: `${NS}.errors.engineUnavailable`,
  [VAULT_PARAMS_ERROR.ENGINE_TIMEOUT]: `${NS}.errors.engineTimeout`,
  [VAULT_PARAMS_ERROR.ENGINE_NOT_CONFIGURED]: `${NS}.errors.engineNotConfigured`,
};

/** Every code this page translates (the i18n test resolves each key). */
export const ENGINE_ERROR_KEYS: Record<string, string> = {
  ...ENGINE_FAILURE_KEYS,
  [VAULT_PARAMS_ERROR.INVALID]: `${NS}.errors.invalid`,
};

/**
 * A rejected value: the new BFF's 422 VAULT_PARAMS_INVALID, or an older BFF's
 * 422 (any code) once the caller has ruled out a conflict by re-reading.
 */
const isRejectedValue = (info: ApiErrorInfo): boolean =>
  info.code === VAULT_PARAMS_ERROR.INVALID || info.status === 422;

/**
 * The save dialog's error list: a translated summary first, then — for a
 * rejected request only (engine value check, or the BFF's own 400 DTO check) —
 * the reason as detail, since it says which value to fix.
 */
export const saveErrorMessages = (
  info: ApiErrorInfo,
  translate: (key: string) => string
): string[] => {
  const failureKey = info.code ? ENGINE_FAILURE_KEYS[info.code] : undefined;
  if (failureKey) return [translate(failureKey)];
  if (isRejectedValue(info)) return [translate(`${NS}.errors.invalid`), ...info.messages];
  if (info.status === 400) return [translate(`${NS}.errors.saveFailed`), ...info.messages];
  return [translate(`${NS}.errors.saveFailed`)];
};

/** The load banner: one translated line; the raw upstream text is not shown. */
export const loadErrorMessage = (info: ApiErrorInfo, translate: (key: string) => string): string =>
  translate((info.code && ENGINE_FAILURE_KEYS[info.code]) || `${NS}.loadFailed`);

/** `dayOne` is the singular of `days`; languages without a plural leave it out. */
export type FieldUnits = Partial<Record<FieldKind, string>> & { dayOne?: string };

/**
 * `units` is optional and defaults to the historical hard-coded symbols
 * (`%`, `×`, `USDC`) so existing call sites and tests keep working. Callers
 * that have a `translate` on hand should build the map with `fieldUnits`
 * below, so the unit text (including `days`/`seconds`, which had none
 * before) comes from i18n instead.
 */
export const formatValue = (kind: FieldKind, raw: unknown, units?: FieldUnits): string => {
  if (raw === null || raw === undefined || raw === '') return EMPTY;
  const unit = units?.[kind];
  switch (kind) {
    case 'percent':
      return `${decimalToPercent(String(raw)) ?? EMPTY}${unit ?? '%'}`;
    case 'multiplier':
      return `${trimZeros(String(raw))}${unit ?? '×'}`;
    case 'usdc':
      return `${trimZeros(String(raw))} ${unit ?? 'USDC'}`;
    case 'days': {
      const dayUnit = Number(raw) === 1 ? (units?.dayOne ?? unit) : unit;
      return dayUnit ? `${raw} ${dayUnit}` : String(raw);
    }
    case 'seconds':
    case 'milliseconds':
      return unit ? `${raw} ${unit}` : String(raw);
    case 'symbols':
      return Array.isArray(raw) && raw.length > 0 ? raw.join(', ') : EMPTY;
    default:
      return String(raw);
  }
};

/** Builds the `formatValue` units map from i18n, for read views and the confirm diff. */
export const fieldUnits = (translate: (key: string) => string): FieldUnits => ({
  usdc: translate(`${NS}.units.usdc`),
  days: translate(`${NS}.units.days`),
  dayOne: translate(`${NS}.units.dayOne`),
  seconds: translate(`${NS}.units.seconds`),
  milliseconds: translate(`${NS}.units.milliseconds`),
  percent: translate(`${NS}.units.percent`),
  multiplier: translate(`${NS}.units.multiplier`),
});

export const toDraft = <T>(value: T, fields: FieldSpec<T>[]): Draft => {
  const draft: Draft = {};
  editableFields(fields).forEach(({ key, kind }) => {
    const raw = value[key] as unknown;
    // Canonical from the start: a stored btc/BTC pair opens as one BTC chip, so
    // the form shows exactly what a save will send (the confirm diff still
    // compares against the stored value and lists the collapse).
    if (kind === 'symbols')
      draft[key] = normalizeCoins(Array.isArray(raw) ? (raw as string[]) : []);
    else if (kind === 'percent') draft[key] = decimalToPercent(String(raw ?? '')) ?? '';
    else if (kind === 'usdc' || kind === 'multiplier') {
      draft[key] = raw === null || raw === undefined ? '' : trimZeros(String(raw));
    } else draft[key] = raw === null || raw === undefined ? '' : String(raw);
  });
  return draft;
};

export const fromDraft = <T>(draft: Draft, fields: FieldSpec<T>[]): T => {
  const value: Record<string, unknown> = {};
  editableFields(fields).forEach(({ key, kind }) => {
    const raw = draft[key];
    if (kind === 'symbols') {
      // Canonicalise here too, so a value stored before the case rule (btc + BTC)
      // collapses on its next save even if the operator never touches the field.
      value[key] = normalizeCoins(Array.isArray(raw) ? raw : []);
    } else if (isIntegerKind(kind)) {
      value[key] = Number.parseInt(String(raw), 10);
    } else if (kind === 'percent') {
      value[key] = percentToDecimal(String(raw)) ?? '';
    } else if (kind === 'usdc' || kind === 'multiplier') {
      value[key] = trimLeadingZeros(String(raw).trim());
    } else {
      value[key] = String(raw).trim();
    }
  });
  return value as T;
};

const str = (draft: Draft, key: string): string => String(draft[key] ?? '').trim();

/** A decimal string in range, with at most `maxDp` places. Number() is used for
 * the range only — the payload is never built from it. */
const checkDecimal = (
  value: string,
  {
    min,
    max,
    minExclusive,
    maxExclusive,
    maxDp,
  }: {
    min: number;
    max?: number;
    minExclusive?: boolean;
    maxExclusive?: boolean;
    maxDp: number;
  }
): string | undefined => {
  if (!value) return `${NS}.errors.required`;
  if (!isNonNegativeDecimalString(value)) return `${NS}.errors.notANumber`;
  if (decimalPlaces(value) > maxDp) return `${NS}.errors.tooManyDecimals`;
  const n = Number(value);
  if (minExclusive ? n <= min : n < min) return `${NS}.errors.outOfRange`;
  if (max !== undefined && (maxExclusive ? n >= max : n > max)) return `${NS}.errors.outOfRange`;
  return undefined;
};

const checkInteger = (value: string, min: number, max?: number): string | undefined => {
  if (!value) return `${NS}.errors.required`;
  if (!/^\d+$/.test(value)) return `${NS}.errors.notAWholeNumber`;
  const n = Number.parseInt(value, 10);
  if (n < min || (max !== undefined && n > max)) return `${NS}.errors.outOfRange`;
  return undefined;
};

const collect = (entries: [string, string | undefined][]): FieldErrors =>
  Object.fromEntries(entries.filter((e): e is [string, string] => Boolean(e[1])));

export const validateCoreDraft = (d: Draft): FieldErrors => {
  const errors = collect([
    [
      'minSubscription',
      checkDecimal(str(d, 'minSubscription'), {
        min: 0,
        minExclusive: true,
        maxDp: DECIMAL_MAX_DECIMALS,
      }),
    ],
    [
      'minRedemption',
      checkDecimal(str(d, 'minRedemption'), {
        min: 0,
        minExclusive: true,
        maxDp: DECIMAL_MAX_DECIMALS,
      }),
    ],
    ['lockupMinDays', checkInteger(str(d, 'lockupMinDays'), LOCKUP_DAYS_MIN, LOCKUP_DAYS_MAX)],
    ['lockupMaxDays', checkInteger(str(d, 'lockupMaxDays'), LOCKUP_DAYS_MIN, LOCKUP_DAYS_MAX)],
    [
      'maxLiveVaultsPerManager',
      checkInteger(
        str(d, 'maxLiveVaultsPerManager'),
        MAX_LIVE_VAULTS_PER_MANAGER_MIN,
        MAX_LIVE_VAULTS_PER_MANAGER_MAX
      ),
    ],
    [
      'performanceFeeCap',
      // 0 is allowed: the cap's PRD range is 0–30% (Module PRD §14.7); a zero
      // cap forbids every performance fee.
      checkDecimal(str(d, 'performanceFeeCap'), {
        min: 0,
        max: PERFORMANCE_FEE_CAP_MAX_PERCENT,
        maxDp: PERCENT_MAX_DECIMALS,
      }),
    ],
    [
      'ownershipFloor',
      checkDecimal(str(d, 'ownershipFloor'), {
        min: 0,
        minExclusive: true,
        max: 100,
        maxExclusive: true,
        maxDp: PERCENT_MAX_DECIMALS,
      }),
    ],
  ]);
  if (
    !errors.lockupMinDays &&
    !errors.lockupMaxDays &&
    Number(str(d, 'lockupMinDays')) > Number(str(d, 'lockupMaxDays'))
  ) {
    errors.lockupMinDays = `${NS}.errors.lockupOrder`;
  }
  return errors;
};

const checkWhitelist = (raw: string | string[] | undefined): string | undefined => {
  const coins = (Array.isArray(raw) ? raw : []).map((s) => s.trim());
  if (coins.length === 0) return `${NS}.errors.whitelistEmpty`;
  if (coins.some((c) => !HL_PERP_COIN.test(c))) return `${NS}.errors.coinFormat`;
  if (new Set(coins).size !== coins.length) return `${NS}.errors.coinDuplicate`;
  return undefined;
};

export const validateRiskDraft = (d: Draft): FieldErrors => {
  const pct = { maxDp: PERCENT_MAX_DECIMALS };
  const errors = collect([
    ['instrumentWhitelist', checkWhitelist(d.instrumentWhitelist)],
    [
      'maxLeverage',
      checkDecimal(str(d, 'maxLeverage'), {
        min: 0,
        minExclusive: true,
        max: MAX_LEVERAGE_CEILING,
        maxDp: DECIMAL_MAX_DECIMALS,
      }),
    ],
    [
      'concentrationCap',
      checkDecimal(str(d, 'concentrationCap'), { ...pct, min: 0, minExclusive: true, max: 100 }),
    ],
    [
      'marginWarning',
      checkDecimal(str(d, 'marginWarning'), {
        ...pct,
        min: 0,
        minExclusive: true,
        max: 100,
        maxExclusive: true,
      }),
    ],
    [
      'forcedDeleverage',
      checkDecimal(str(d, 'forcedDeleverage'), {
        ...pct,
        min: 0,
        minExclusive: true,
        max: 100,
        maxExclusive: true,
      }),
    ],
    [
      'slippageTolerance',
      checkDecimal(str(d, 'slippageTolerance'), { ...pct, min: 0, max: 100, maxExclusive: true }),
    ],
    [
      'closeTimeoutSeconds',
      checkInteger(str(d, 'closeTimeoutSeconds'), 1, CLOSE_TIMEOUT_SECONDS_MAX),
    ],
    [
      'confirmCloseThreshold',
      checkDecimal(str(d, 'confirmCloseThreshold'), { ...pct, min: 0, max: 100 }),
    ],
    [
      'eventTimeToleranceMs',
      checkInteger(str(d, 'eventTimeToleranceMs'), 1, EVENT_TIME_TOLERANCE_MS_MAX),
    ],
    [
      'venueLagAllowanceMs',
      checkInteger(str(d, 'venueLagAllowanceMs'), 1, VENUE_LAG_ALLOWANCE_MS_MAX),
    ],
    ['maxVenueReadSpanMs', checkInteger(str(d, 'maxVenueReadSpanMs'), 1)],
    [
      'venueTimeSkewBandMs',
      checkInteger(str(d, 'venueTimeSkewBandMs'), 1, VENUE_TIME_SKEW_BAND_MS_MAX),
    ],
  ]);
  if (
    !errors.marginWarning &&
    !errors.forcedDeleverage &&
    Number(str(d, 'marginWarning')) >= Number(str(d, 'forcedDeleverage'))
  ) {
    errors.marginWarning = `${NS}.errors.warningBelowDeleverage`;
  }
  // Engine: max_venue_read_span_ms + venue_lag_allowance_ms <= venue_time_skew_band_ms.
  if (
    !errors.maxVenueReadSpanMs &&
    !errors.venueLagAllowanceMs &&
    !errors.venueTimeSkewBandMs &&
    Number(str(d, 'maxVenueReadSpanMs')) + Number(str(d, 'venueLagAllowanceMs')) >
      Number(str(d, 'venueTimeSkewBandMs'))
  ) {
    errors.venueTimeSkewBandMs = `${NS}.errors.venueReadSpanExceedsBand`;
  }
  return errors;
};

/** Equal by VALUE: `0.3` and `0.300000` are the same stored decimal. */
const sameValue = (kind: FieldKind, a: unknown, b: unknown): boolean => {
  if (kind === 'symbols') return JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
  if (isIntegerKind(kind)) return Number(a) === Number(b);
  return trimZeros(String(a ?? '')) === trimZeros(String(b ?? ''));
};

/** Split out so `prev` stays a plain, un-narrowed `T` — narrowing `T | null` to `T` via
 * control flow makes generic indexed access (`prev[key]`) look possibly-undefined to tsc. */
const diffAgainst = <T>(
  prev: T,
  after: T,
  fields: FieldSpec<T>[],
  units?: FieldUnits
): FieldChange[] =>
  editableFields(fields)
    .filter(({ key, kind }) => !sameValue(kind, prev[key], after[key]))
    .map(({ key, kind, label }) => ({
      key,
      label,
      before: formatValue(kind, prev[key], units),
      after: formatValue(kind, after[key], units),
    }));

export const diffFields = <T>(
  before: T | null,
  after: T,
  fields: FieldSpec<T>[],
  units?: FieldUnits
): FieldChange[] => {
  if (before === null) {
    return editableFields(fields).map(({ key, kind, label }) => ({
      key,
      label,
      before: EMPTY,
      after: formatValue(kind, after[key], units),
    }));
  }
  return diffAgainst(before, after, fields, units);
};
